import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './StudentDashboard.css';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';

const FeeDetailsCard = ({ userId: propUserId, classId: propClassId }) => {
  const { axios, token, user, studentInfo } = useAuth();
  const userId = propUserId || studentInfo?.userId || user?.userId || user?.id;
  const classId = propClassId || studentInfo?.classId || user?.studentInfo?.classId || user?.classId;

  const [feeStructure, setFeeStructure] = useState([]);
  const [feeStructureId, setFeeStructureId] = useState(null);
  const [feePayments, setFeePayments] = useState([]);
  const [selectedInstallmentId, setSelectedInstallmentId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch fee structure
  const fetchFeeStructure = useCallback(async () => {
    if (!classId) {
      setFeeStructure([]);
      return;
    }
    const TARGET_YEAR = '2025-2026';
    try {
      const response = await axios.get(
        `${configData.GET_FEE_STRUCTURE}/class/${classId}?academicYear=${TARGET_YEAR}`,
        {
          headers: {
            accept: 'application/hal+json',
            Authorization: token,
          },
        }
      );
      const feeData = Array.isArray(response.data)
        ? response.data.find((i) => i.academicYear === TARGET_YEAR) || response.data[0]
        : response.data;
      const rawInstallments =
        feeData?.feeInstallments || feeData?.installments || (Array.isArray(response.data) ? response.data : []);
      // Filter out total annual summary installment
      const termInstallments = (Array.isArray(rawInstallments) ? rawInstallments : []).filter(
        (i) => !i?.isAnnual && !i?.description?.toLowerCase().includes('total annual')
      );
      setFeeStructure(termInstallments);
      setFeeStructureId(feeData?.feeStructureId || null);
    } catch (error) {
      console.error('Error fetching fee structure:', error);
      setFeeStructure([]);
    }
  }, [axios, classId, token]);

  // Fetch fee payments for the student
  const fetchFeePayments = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }
    try {
      let paymentsList = [];
      try {
        const response = await axios.get(`${configData.INFRASTRUCTURE_FEEPAYMENT}/student/${userId}`, {
          headers: { accept: 'application/hal+json' },
        });
        paymentsList = Array.isArray(response.data)
          ? response.data
          : response.data?.items || response.data?.content || [];
      } catch (e) {
        paymentsList = [];
      }

      if (paymentsList.length === 0) {
        try {
          const userResponse = await axios.get(`${configData.INFRASTRUCTURE_FEEPAYMENT}/user/${userId}`, {
            headers: { accept: 'application/hal+json' },
          });
          paymentsList = Array.isArray(userResponse.data)
            ? userResponse.data
            : userResponse.data?.items || userResponse.data?.content || [];
        } catch (e) {
          // ignore
        }
      }
      setFeePayments(paymentsList);
    } catch (error) {
      console.error('Error fetching fee payments:', error);
      setFeePayments([]);
    } finally {
      setLoading(false);
    }
  }, [axios, userId]);

  useEffect(() => {
    fetchFeeStructure();
    fetchFeePayments();
  }, [fetchFeeStructure, fetchFeePayments]);

  // Helper to extract fee amount from an installment
  const getInstallmentFee = useCallback((installment) => {
    if (!installment) return 0;
    if (installment.feeBreakdown && typeof installment.feeBreakdown === 'object') {
      const breakdownSum = Object.values(installment.feeBreakdown).reduce((s, v) => s + (Number(v) || 0), 0);
      if (breakdownSum > 0) return breakdownSum;
    }
    return Number(
      installment.totalFee ||
      installment.totalAmount ||
      installment.amount ||
      installment.installmentAmount ||
      0
    );
  }, []);

  // Determine current quarter based on date
  const getCurrentQuarter = useCallback(() => {
    if (!Array.isArray(feeStructure) || feeStructure.length === 0) return null;
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // 1-12
    let quarterDescription = 'Quarter 1 (April-June)';
    if (currentMonth >= 7 && currentMonth <= 9) quarterDescription = 'Quarter 2 (July-September)';
    else if (currentMonth >= 10 && currentMonth <= 12) quarterDescription = 'Quarter 3 (October-December)';
    else if (currentMonth >= 1 && currentMonth <= 3) quarterDescription = 'Quarter 4 (January-March)';

    const selectedQuarter = feeStructure.find(
      (inst) =>
        inst?.description === quarterDescription ||
        (currentMonth <= 9 && inst?.description?.toLowerCase().includes('term 1')) ||
        (currentMonth > 9 && currentMonth <= 12 && inst?.description?.toLowerCase().includes('term 2')) ||
        (currentMonth < 4 && inst?.description?.toLowerCase().includes('term 3'))
    );
    return selectedQuarter || feeStructure[0] || null;
  }, [feeStructure]);

  // Default selected installment when feeStructure loads
  useEffect(() => {
    if (Array.isArray(feeStructure) && feeStructure.length > 0) {
      const currentQ = getCurrentQuarter();
      const defaultId =
        currentQ?.installmentId || currentQ?.id || feeStructure[0]?.installmentId || feeStructure[0]?.id;
      setSelectedInstallmentId((prev) => prev || defaultId);
    }
  }, [feeStructure, getCurrentQuarter]);

  // Get matching payment for an installment
  const getPaymentForInstallment = useCallback(
    (installmentId) => {
      if (!installmentId) return null;
      const payments = Array.isArray(feePayments) ? feePayments : [];
      // Look for SUCCESS payment first
      const successPayment = payments.find(
        (p) =>
          (p?.installmentId === installmentId || p?.installmentId === 'ANNUAL_TOTAL') &&
          (!p?.feeStructureId || !feeStructureId || p?.feeStructureId === feeStructureId) &&
          ['SUCCESS', 'COMPLETED', 'PAID'].includes(String(p?.paymentStatus || p?.status || '').toUpperCase())
      );
      if (successPayment) return successPayment;

      // Look for pending payment
      return payments.find(
        (p) =>
          (p?.installmentId === installmentId || p?.installmentId === 'ANNUAL_TOTAL') &&
          (!p?.feeStructureId || !feeStructureId || p?.feeStructureId === feeStructureId) &&
          ['PENDING', 'PENDING_VERIFICATION', 'SUBMITTED'].includes(String(p?.paymentStatus || p?.status || '').toUpperCase())
      );
    },
    [feePayments, feeStructureId]
  );

  // Active installment (selected by user or defaulted to current quarter)
  const activeInstallment = useMemo(() => {
    if (!Array.isArray(feeStructure) || feeStructure.length === 0) return null;
    return (
      feeStructure.find((inst) => (inst?.installmentId || inst?.id) === selectedInstallmentId) ||
      getCurrentQuarter() ||
      feeStructure[0]
    );
  }, [feeStructure, selectedInstallmentId, getCurrentQuarter]);

  // Calculate annual summary
  const annualSummary = useMemo(() => {
    if (!Array.isArray(feeStructure) || feeStructure.length === 0) {
      return { totalAmount: 0, totalPaid: 0, remainingAmount: 0 };
    }
    const totalAmount = feeStructure.reduce((sum, inst) => sum + getInstallmentFee(inst), 0);
    const payments = Array.isArray(feePayments) ? feePayments : [];
    const paidAmount = payments
      .filter(
        (p) =>
          (!feeStructureId || p?.feeStructureId === feeStructureId) &&
          ['SUCCESS', 'COMPLETED', 'PAID'].includes(String(p?.paymentStatus || p?.status || '').toUpperCase())
      )
      .reduce((sum, p) => sum + Number(p?.amountPaid || p?.amount || 0), 0);

    const remainingAmount = Math.max(0, totalAmount - paidAmount);
    return { totalAmount, totalPaid: paidAmount, remainingAmount };
  }, [feeStructure, feePayments, feeStructureId, getInstallmentFee]);

  if (loading) {
    return (
      <div className="sd-card sd-fee-card">
        <div className="sd-card-header">
          <h3>Fee Summary</h3>
        </div>
        <div style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
          Loading fee details...
        </div>
      </div>
    );
  }

  const activeFee = getInstallmentFee(activeInstallment);
  const activePayment = getPaymentForInstallment(activeInstallment?.installmentId || activeInstallment?.id);
  const amountPaid = Number(activePayment?.amountPaid || activePayment?.amount || 0);

  const rawStatus = String(activePayment?.paymentStatus || activePayment?.status || '').toUpperCase();
  const isPaid = ['SUCCESS', 'COMPLETED', 'PAID'].includes(rawStatus) && (activeFee === 0 || amountPaid >= activeFee);
  const isPartial = ['SUCCESS', 'COMPLETED', 'PAID'].includes(rawStatus) && amountPaid > 0 && amountPaid < activeFee;
  const isPendingVerification = ['PENDING', 'PENDING_VERIFICATION', 'SUBMITTED'].includes(rawStatus);

  let statusText = 'Pending';
  let statusClass = 'pending';
  if (isPaid) {
    statusText = 'Paid';
    statusClass = 'paid';
  } else if (isPartial) {
    statusText = 'Partial';
    statusClass = 'partial';
  } else if (isPendingVerification) {
    statusText = 'Verification Pending';
    statusClass = 'pending';
  }

  return (
    <div className="sd-card sd-fee-card">
      <div className="sd-card-header">
        <h3>Fee Summary</h3>
        {feeStructure.length > 0 && (
          <div className="sd-dropdown">
            <select
              value={selectedInstallmentId || ''}
              onChange={(e) => setSelectedInstallmentId(e.target.value)}
            >
              {feeStructure.map((installment, index) => {
                const id = installment?.installmentId || installment?.id || `inst-${index}`;
                return (
                  <option key={id} value={id}>
                    {installment?.description || `Installment ${index + 1}`}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>

      <div className="sd-fee-summary-section">
        <div className="sd-fee-item1">
          <label>Due Date</label>
          <span>
            {activeInstallment?.dueDate
              ? new Date(activeInstallment.dueDate).toLocaleDateString('en-GB')
              : 'N/A'}
          </span>
        </div>
        <div className="sd-fee-item1">
          <label>Installment Fee</label>
          <span>₹{activeFee.toLocaleString('en-IN')}</span>
        </div>
        <div className="sd-fee-item1">
          <label>Amount Paid</label>
          <span>₹{amountPaid.toLocaleString('en-IN')}</span>
        </div>
        <div className="sd-fee-item1">
          <label>Payment Status</label>
          <span className={`sd-payment-status ${statusClass}`}>
            {statusText}
          </span>
        </div>
      </div>

      <div className="sd-annual-summary-section">
        <h4>Annual Summary</h4>
        <div className="sd-fee-item">
          <label>Total Amount for Year</label>
          <span>₹{(annualSummary?.totalAmount || 0).toLocaleString('en-IN')}</span>
        </div>
        <div className="sd-fee-item sd-paid">
          <label>Total Amount Paid</label>
          <span>₹{(annualSummary?.totalPaid || 0).toLocaleString('en-IN')}</span>
        </div>
        <div className="sd-fee-item sd-remaining">
          <label>Remaining Amount</label>
          <span>₹{(annualSummary?.remainingAmount || 0).toLocaleString('en-IN')}</span>
        </div>
      </div>
    </div>
  );
};

export default FeeDetailsCard;